import { useResource } from '../../lib/hooks';
import type { Department, Employee } from '../../lib/types';

/**
 * Who owns the relationship, chosen from the Sales department.
 *
 * A dropdown rather than free text because the pipeline is GROUPED by this
 * value: "Cam", "cam" and "Cam Whitfield" are three account managers on that
 * page, and typing is how you get all three.
 *
 * The value is `core.Employee.EmployeeId` — the reference the shared-database
 * contract asks for (rule 3), not the spelling of somebody's name. See
 * migration 023. The name shown everywhere else is resolved by the API from
 * that id, so a rename in the directory follows through instead of orphaning
 * every client the person owns.
 *
 * `currentName` is the resolved name already on the record. It only matters
 * when the person is no longer in Sales: they stay selectable so that merely
 * opening the form cannot quietly reassign the account.
 */
export function AccountManagerPicker({
  value,
  currentName,
  onChange,
  onPickEmployee,
}: {
  value: number | null;
  currentName?: string | null;
  onChange: (next: number | null) => void;
  /** The chosen person, for a caller that needs more than the id (the Autotask import matches them by email or name). */
  onPickEmployee?: (employee: Employee | null) => void;
}) {
  const departments = useResource<Department[]>('/departments');
  const sales = (departments.data ?? []).find((d) => d.Name.toLowerCase() === 'sales') ?? null;

  const employees = useResource<Employee[]>(
    sales ? `/employees?status=active&departmentId=${sales.DepartmentId}` : null,
  );
  const people = employees.data ?? [];

  /* Somebody who has left Sales, or left the company. The id is still valid and
     still points at a real person, so it is kept as an option rather than
     silently reset. */
  const orphaned = value !== null && !people.some((e) => e.EmployeeId === value);

  if (departments.loading || employees.loading) {
    return (
      <select className="select" disabled>
        <option>Loading…</option>
      </select>
    );
  }

  /* Said plainly rather than shown as an empty dropdown, which reads as broken
     rather than as "nobody is in that department yet". */
  if (!sales) {
    return (
      <>
        <select className="select" disabled>
          <option>No Sales department</option>
        </select>
        <span className="xs subtle">
          Create a department called “Sales” on the Employees page, then add people to it.
        </span>
      </>
    );
  }

  if (people.length === 0 && !orphaned) {
    return (
      <>
        <select className="select" disabled>
          <option>Nobody in Sales yet</option>
        </select>
        <span className="xs subtle">
          Add someone to the Sales department on the Employees page and they will appear here.
        </span>
      </>
    );
  }

  return (
    <select
      className="select"
      value={value === null ? '' : String(value)}
      onChange={(e) => {
        const id = e.target.value === '' ? null : Number(e.target.value);
        onChange(id);
        onPickEmployee?.(id === null ? null : (people.find((p) => p.EmployeeId === id) ?? null));
      }}
    >
      <option value="">Unassigned</option>
      {people.map((e) => (
        <option key={e.EmployeeId} value={e.EmployeeId}>
          {e.FullName}
          {e.RoleTitle ? ` — ${e.RoleTitle}` : ''}
        </option>
      ))}
      {orphaned && (
        <option value={value}>{currentName?.trim() || `Employee #${value}`} (not in Sales)</option>
      )}
    </select>
  );
}
